import { AppSettings } from '../app-settings';
import { parseWallClock } from './profile-dates.util';

/**
 * Añadir un evento al calendario.
 *
 * No hay una forma única que valga para todos:
 *
 *  - Google Calendar y Outlook tienen una URL que abre el evento ya relleno.
 *    Es la web: ni Google ni Microsoft ofrecen un enlace que abra su app con el
 *    evento preparado. En Android se intenta de todas formas con Google (ver
 *    `openGoogleCalendar`); en iPhone no hay manera.
 *  - Apple (iPhone, iPad, Mac) no tiene nada parecido: entiende el .ics. Y
 *    tiene que llegarle por una URL de verdad (`icsUrl`), no descargado desde
 *    un blob: así el iPhone saca la hoja de "Añadir al calendario" en vez de
 *    guardarlo en Descargas. Esa hoja deja elegir cualquier calendario que
 *    tenga el teléfono, también una cuenta de Google o de Outlook, así que en
 *    iPhone es la opción buena para todos.
 *
 * Las horas van tal cual están guardadas —hora de pared de Madrid— y se le
 * dice al calendario en qué zona interpretarlas (`ctz` en Google, `TZID` en
 * el .ics). Así no hay que calcular el desfase de verano, que es justo donde
 * se cuelan las horas mal.
 */

const ZONA = 'Europe/Madrid';

/** La API no guarda hora de fin, así que se reserva una franja de dos horas. */
const DURACION_HORAS = 2;

export interface CalendarEvent {
  title: string;
  /** Hora de pared de Madrid, como viene de la API. */
  startDate: string | null;
  location?: string | null;
  /** Con estos dos la API monta el .ics (ver `icsUrl`). */
  eventId?: number | null;
  sessionId?: number | null;
}

/**
 * En qué dispositivo se está, para ordenar las opciones del menú.
 *
 * El iPad hay que reconocerlo por el táctil: desde iPadOS 13 Safari se anuncia
 * como `Macintosh` y por el user agent no se distingue de un Mac.
 */
export function calendarPlatform(): 'ios' | 'mac' | 'android' | 'other' {
  const userAgent = navigator.userAgent || '';
  const isIpadOs = /Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;

  if (/iPad|iPhone|iPod/.test(userAgent) || isIpadOs) {
    return 'ios';
  }
  if (/Android/.test(userAgent)) {
    return 'android';
  }
  if (/Macintosh/.test(userAgent)) {
    return 'mac';
  }
  return 'other';
}

/**
 * iPhone o iPad con un navegador que no es Safari: Chrome, Firefox, Edge o el
 * navegador interno de Instagram, WhatsApp, Gmail… En iOS solo Safari pasa el
 * .ics a la app Calendario; en los demás el enlace no hace nada (limitación
 * de iOS, no del .ics). Ahí la opción del calendario del iPhone se cambia por
 * un aviso de abrir la página en Safari.
 *
 * Safari se anuncia con `Safari/` y sin ninguna de esas marcas; los navegadores
 * internos de las apps no llevan `Safari/`.
 */
export function iosSinSafari(): boolean {
  if (calendarPlatform() !== 'ios') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  return !/Safari\//.test(userAgent) || /CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|YaBrowser|DuckDuckGo/.test(userAgent);
}

const dos = (n: number) => (n < 10 ? `0${n}` : `${n}`);

/** "20261004T203000" a partir de la hora de pared, sin tocar zonas. */
function marca(valor: string | null | undefined, masHoras: number = 0): string | null {
  const w = parseWallClock(valor);
  if (!w) {
    return null;
  }

  const d = new Date(w.year, w.month - 1, w.day, w.hour + masHoras, w.minute);

  return `${d.getFullYear()}${dos(d.getMonth() + 1)}${dos(d.getDate())}T${dos(d.getHours())}${dos(d.getMinutes())}00`;
}

/**
 * La URL que abre Google Calendar con el evento preparado. `ctz` es lo que
 * hace que las 20:30 sean las 20:30 de Madrid y no las del navegador.
 */
export function googleCalendarUrl(evento: CalendarEvent): string | null {
  const inicio = marca(evento.startDate);
  const fin = marca(evento.startDate, DURACION_HORAS);

  if (!inicio || !fin) {
    return null;
  }

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: evento.title || '',
    dates: `${inicio}/${fin}`,
    ctz: ZONA
  });

  if (evento.location) {
    params.set('location', evento.location);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Abre Google Calendar con el evento relleno.
 *
 * En Android se le pide a Chrome que lo abra con la app de Calendar (un
 * `intent://` con el paquete). La URL es la de siempre con `/event` en vez de
 * `/render`: es la forma antigua, que es la que la app sabe recoger. Si no
 * está instalada o no la recoge, Chrome tira de `browser_fallback_url` y se
 * acaba en la web, igual que antes. Tiene que ser en la misma pestaña: un
 * `intent://` en una pestaña nueva se queda en blanco.
 *
 * OJO: sin probar en un Android de verdad.
 */
export function openGoogleCalendar(evento: CalendarEvent): void {
  const web = googleCalendarUrl(evento);
  if (!web) {
    return;
  }

  if (calendarPlatform() !== 'android') {
    window.open(web, '_blank');
    return;
  }

  const ruta = web.replace('https://', '').replace('/calendar/render?', '/calendar/event?');
  window.location.href = `intent://${ruta}#Intent;scheme=https;package=com.google.android.calendar;`
    + `S.browser_fallback_url=${encodeURIComponent(web)};end`;
}

/**
 * El instante UTC que corresponde a una hora de pared de Madrid.
 *
 * Outlook no tiene nada como el `ctz` de Google, así que hay que darle la
 * hora ya resuelta. En vez de apañarlo con un "+1 o +2 según el mes" —que
 * falla los fines de semana del cambio de hora— se le pregunta al navegador:
 * se prueba con la hora como si fuera UTC, se mira qué hora de Madrid sale y
 * la diferencia es el desfase que hay que aplicar.
 */
function instanteUtc(valor: string | null | undefined): Date | null {
  const w = parseWallClock(valor);
  if (!w) {
    return null;
  }

  const comoSiFueraUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute);

  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  }).formatToParts(new Date(comoSiFueraUtc));

  const dato = (tipo: string) => Number(partes.find(p => p.type === tipo)?.value || 0);

  const enMadrid = Date.UTC(
    dato('year'), dato('month') - 1, dato('day'),
    dato('hour') % 24, dato('minute'), dato('second')
  );

  return new Date(comoSiFueraUtc + (comoSiFueraUtc - enMadrid));
}

/**
 * La URL que abre Outlook con el evento preparado.
 *
 * Va contra `outlook.live.com`, que es el de las cuentas personales
 * (outlook.com, hotmail). Microsoft tiene otro sitio distinto para las
 * cuentas de empresa (`outlook.office.com`) y NO hay ninguna URL que sirva
 * para las dos: si entras con una cuenta de trabajo, Outlook no reconoce la
 * sesión y te la vuelve a pedir. Se elige la personal porque quien compra
 * entradas lo hace con su cuenta de casa; para el resto está el .ics, que
 * Outlook también importa.
 */
export function outlookCalendarUrl(evento: CalendarEvent): string | null {
  const inicio = instanteUtc(evento.startDate);

  if (!inicio) {
    return null;
  }

  const fin = new Date(inicio.getTime() + DURACION_HORAS * 3600 * 1000);
  // Sin milisegundos: Outlook se atraganta con el formato largo.
  const iso = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: iso(inicio),
    enddt: iso(fin),
    subject: evento.title || ''
  });

  if (evento.location) {
    params.set('location', evento.location);
  }

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * La URL del .ics, que lo monta la API (`EventCalendarController`) con los
 * datos públicos del evento. Se navega a ella: en iPhone abre la hoja de
 * "Añadir al calendario", en Mac el Calendario y en el resto se descarga y lo
 * abre la app de calendario que haya (Samsung, Outlook de escritorio...).
 */
export function icsUrl(evento: CalendarEvent): string | null {
  if (!evento.eventId) {
    return null;
  }

  const sesion = evento.sessionId ? `?session=${evento.sessionId}` : '';
  return `${AppSettings.API_URI}/events/${evento.eventId}/calendar${sesion}`;
}
